import React, { useState } from 'react'
import { css, SerializedStyles } from '@emotion/core'
import { Dropdown } from 'antd'
import { useVisibleLineCount } from '@/hooks/use-visible-line-count'

type PanelPlacement = 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight'

type Props = {
  /** 表示したい文章。一覧では省略され、ホバー時のパネルでは全文が出る */
  text: string | number | null | undefined
  /** text が空のときに代わりに表示する文字列 */
  emptyText?: string
  /** 最低でもこの行数は表示する */
  minLines?: number
  /** 文字1行分の高さ(px) */
  lineHeightPx?: number
  /** 親の高さに余裕があれば、その高さいっぱいまで表示するか。false なら常に minLines 行で切る */
  fillParentHeight?: boolean
  /** 一覧側の表示を差し替えたいときに渡す（リンクにしたい場合など）。パネルには常に text が出る */
  children?: React.ReactNode
  /** 一覧側の見た目への追加スタイル */
  customStyle?: SerializedStyles
  /** ホバー時のパネルへの追加スタイル */
  customPanelStyle?: SerializedStyles
  placement?: PanelPlacement
}

/**
 * 長い文章を指定行数で切って末尾を … にし、切れているときだけホバーで全文をパネル表示する。
 * 表示行数は「最低 minLines 行、親の高さに余裕があればその高さいっぱいまで」で決まる。
 *
 * 一覧側は「高さ決め係」と「表示係」の2枚重ねになっている。
 * 表示係をそのまま置くと文章が長いほど行がどんどん高くなり、さらに
 * 他の理由（同じ行のセルが編集モードで広がるなど）で一度伸びた高さを
 * 自分の中身で維持してしまい元に戻らなくなるため、表示係は浮かせて場所を取らせていない。
 */
export const TruncateTooltip: React.FC<Props> = ({
  text,
  emptyText = '-',
  minLines = 2,
  lineHeightPx = 21,
  fillParentHeight = true,
  children,
  customStyle,
  customPanelStyle,
  placement = 'bottomLeft',
}) => {
  const { measureRef, textRef, visibleLineCount, measureIsTextCut } = useVisibleLineCount<HTMLDivElement>({ minLines, lineHeightPx })
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const hasText = text !== null && text !== undefined && text !== ''
  const displayText = hasText ? text : emptyText

  // 文章が切れているときだけパネルを出す（全文が見えているならパネルは不要）
  const handleVisibleChange = (visible: boolean) => setIsPanelOpen(visible && measureIsTextCut())

  return (
    <Dropdown
      // ホバーしたときに出るパネル
      overlay={<div css={[panelStyle, customPanelStyle]}>{text}</div>}
      visible={isPanelOpen}
      onVisibleChange={handleVisibleChange}
      trigger={['hover']}
      placement={placement}
      // 背の高いセルでは上下に余白が残らないため、セルの右隣に上端を揃えて出す
      align={{ points: ['tl', 'tr'], offset: [8, 0], overflow: { adjustX: true, adjustY: true } }}
      mouseEnterDelay={0.3}
    >
      {/* ここから下が一覧に表示される中身。この部分にマウスが乗るとパネルが出る */}
      <div css={[wrapperStyle(fillParentHeight), customStyle]}>
        {/*
          高さを決める。見えないが場所は取る。
          常に minLines 行分しか場所を取らないので、どれだけ長い文章でも行がそれ以上高くならない
        */}
        <div css={[textAppearanceStyle(lineHeightPx), truncateStyle(minLines), heightKeeperStyle]} aria-hidden>
          {displayText}
        </div>
        {/*
          表示をする。浮かせてあるので場所を取らず、行の高さに影響しない。
          高さ決め係が確保した領域にぴったり重なるので、この要素の高さ = 表示に使える高さになる
        */}
        <div ref={measureRef} css={floatingLayerStyle}>
          <div ref={textRef} css={[textAppearanceStyle(lineHeightPx), truncateStyle(visibleLineCount)]}>
            {children ?? displayText}
          </div>
        </div>
      </div>
    </Dropdown>
  )
}

// 一覧側の外枠。中の「浮かせた要素」の位置の基準になる
const wrapperStyle = (fillParentHeight: boolean) =>
  css({
    minWidth: 0,
    position: 'relative',
    width: '100%',
    // 親要素が flex のときに、その高さいっぱいまで縦に伸びる。
    // 同じ行の他の列で行が高くなっているとき、その高さを受け取るための指定
    ...(fillParentHeight ? { alignSelf: 'stretch' } : {}),
  })

// 見えないが場所は取る。行の高さはこの要素だけで決まる
const heightKeeperStyle = css({
  visibility: 'hidden',
})

// 浮かせて場所を取らなくする。外枠と同じ大きさにぴったり重なる。
// 浮かせた要素では文章を切る CSS が効かなくなるので、切るのは中の要素の担当
const floatingLayerStyle = css({
  bottom: 0,
  left: 0,
  overflow: 'hidden',
  position: 'absolute',
  right: 0,
  top: 0,
})

// 文字の見た目。1行の高さと、長い単語や改行の扱い
const textAppearanceStyle = (lineHeightPx: number) =>
  css({
    lineHeight: `${lineHeightPx}px`,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  })

// 指定した行数で切り、続きがあれば末尾を … にする
const truncateStyle = (lineCount: number) =>
  css({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lineCount,
    overflow: 'hidden',
  })

// ホバー時のパネルの見た目はここだけ触れば変えられる。
// 色や余白はテーブル（restaurant-table-section.tsx）と揃えてある
const panelStyle = css({
  background: '#FFF',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  boxShadow: '0 3px 10px #0000003d',
  color: '#272727',
  fontSize: 14,
  lineHeight: '21px',
  padding: '10px 12px',
  maxWidth: 420,
  // これを超える長文だけスクロールする
  maxHeight: '50vh',
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
})
